import React, { useState } from 'react'
import {
  Users,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Clock,
  Star,
  Award,
  Target,
  BarChart3,
  Activity
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAgencyDashboard } from '@/hooks/useAgencyData'

interface AgencyStats {
  totalCounselors: number
  activeCounselors: number
  totalStudents: number
  activeStudents: number
  totalSessions: number
  completedSessions: number
  totalRevenue: number
  monthlyRevenue: number
  averageRating: number
  responseTime: number
}

interface PerformanceMetric {
  counselorName: string
  totalSessions: number
  completedSessions: number
  averageRating: number
  totalRevenue: number
  responseTime: number
  studentCount: number
}

export const AgencyDashboard: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<'week' | 'month' | 'quarter'>('month')

  // Get dynamic agency data
  const { data: dashboardData, isLoading, error } = useAgencyDashboard()
  
  // Use real data or fallback to defaults while loading
  const agencyStats: AgencyStats = dashboardData?.stats || {
    totalCounselors: 0,
    activeCounselors: 0,
    totalStudents: 0,
    activeStudents: 0,
    totalSessions: 0,
    completedSessions: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    averageRating: 0,
    responseTime: 0
  }

  // Use real counselor performance data
  const counselorPerformance: PerformanceMetric[] = dashboardData?.counselorPerformance || []
  
  // Use real goals or defaults
  const goals = dashboardData?.goals || {
    revenueTarget: 30000,
    sessionsTarget: 1500,
    studentEngagementTarget: 200
  }

  const getChangeIcon = (trend: 'up' | 'down' | 'neutral') => {
    if (trend === 'up') return TrendingUp
    if (trend === 'down') return TrendingDown
    return Activity
  }

  const getChangeColor = (trend: 'up' | 'down' | 'neutral') => {
    if (trend === 'up') return 'text-green-600'
    if (trend === 'down') return 'text-red-600'
    return 'text-gray-600'
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading agency dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading dashboard data</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const StatCard: React.FC<{
    title: string
    value: string | number
    change: string
    trend: 'up' | 'down' | 'neutral'
    icon: React.ElementType
    color: string
  }> = ({ title, value, change, trend, icon: Icon, color }) => {
    const ChangeIcon = getChangeIcon(trend)
    
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
            <div className="flex items-center mt-2">
              <ChangeIcon className={cn("h-4 w-4 mr-1", getChangeColor(trend))} />
              <span className={cn("text-sm font-medium", getChangeColor(trend))}>
                {change}
              </span>
              <span className="text-sm text-gray-500 ml-1">vs last {selectedTimeframe}</span>
            </div>
          </div>
          <div className={cn(
            "p-3 rounded-lg",
            color === 'blue' && "bg-blue-100",
            color === 'green' && "bg-green-100",
            color === 'yellow' && "bg-yellow-100",
            color === 'purple' && "bg-purple-100",
            color === 'red' && "bg-red-100"
          )}>
            <Icon className={cn(
              "h-6 w-6",
              color === 'blue' && "text-blue-600",
              color === 'green' && "text-green-600",
              color === 'yellow' && "text-yellow-600",
              color === 'purple' && "text-purple-600",
              color === 'red' && "text-red-600"
            )} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agency Dashboard</h1>
          <p className="text-gray-600 mt-1">Monitor your agency's performance and counselor metrics</p>
        </div>
        <div className="flex bg-white border border-gray-300 rounded-lg p-1 mt-4 sm:mt-0">
          {(['week', 'month', 'quarter'] as const).map((timeframe) => (
            <button
              key={timeframe}
              onClick={() => setSelectedTimeframe(timeframe)}
              className={cn(
                "px-3 py-1 rounded-md text-sm font-medium transition-colors",
                selectedTimeframe === timeframe
                  ? "bg-primary text-white"
                  : "text-gray-600 hover:text-gray-900"
              )}
            >
              {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Revenue"
          value={`$${agencyStats.totalRevenue.toLocaleString()}`}
          change="+12.5%"
          trend="up"
          icon={DollarSign}
          color="green"
        />
        <StatCard
          title="Active Counselors"
          value={`${agencyStats.activeCounselors}/${agencyStats.totalCounselors}`}
          change="+2"
          trend="up"
          icon={Users}
          color="blue"
        />
        <StatCard
          title="Completed Sessions"
          value={agencyStats.completedSessions}
          change="+8.3%"
          trend="up"
          icon={Calendar}
          color="purple"
        />
        <StatCard
          title="Average Rating"
          value={agencyStats.averageRating}
          change="+0.2"
          trend="up"
          icon={Star}
          color="yellow"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Overview */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Counselor Performance</h2>
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-500">Top Performers</span>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Counselor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sessions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rating
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Revenue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Response Time
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {counselorPerformance.map((counselor, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-medium">
                              {counselor.counselorName.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">{counselor.counselorName}</p>
                            <p className="text-xs text-gray-500">{counselor.studentCount} students</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{counselor.completedSessions}/{counselor.totalSessions}</div>
                        <div className="text-xs text-gray-500">
                          {Math.round((counselor.completedSessions / counselor.totalSessions) * 100)}% completion
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-400 fill-current mr-1" />
                          <span className="text-sm text-gray-900">{counselor.averageRating}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${counselor.totalRevenue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={cn(
                          "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                          counselor.responseTime <= 10 ? "bg-green-100 text-green-800" :
                          counselor.responseTime <= 20 ? "bg-yellow-100 text-yellow-800" :
                          "bg-red-100 text-red-800"
                        )}>
                          {counselor.responseTime}m
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Quick Stats & Goals */}
        <div className="space-y-6">
          {/* Monthly Goals */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Goals</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Revenue Target</span>
                  <span className="font-medium">${agencyStats.monthlyRevenue.toLocaleString()} / ${goals.revenueTarget.toLocaleString()}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full" 
                    style={{ width: `${Math.min((agencyStats.monthlyRevenue / goals.revenueTarget) * 100, 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{Math.round((agencyStats.monthlyRevenue / goals.revenueTarget) * 100)}% of monthly target</p>
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Sessions Target</span>
                  <span className="font-medium">{agencyStats.completedSessions} / {goals.sessionsTarget.toLocaleString()}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${Math.min((agencyStats.completedSessions / goals.sessionsTarget) * 100, 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{Math.round((agencyStats.completedSessions / goals.sessionsTarget) * 100)}% of monthly target</p>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Student Engagement</span>
                  <span className="font-medium">{agencyStats.activeStudents} / {agencyStats.totalStudents}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full" 
                    style={{ width: `${(agencyStats.activeStudents / agencyStats.totalStudents) * 100}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{Math.round((agencyStats.activeStudents / Math.max(agencyStats.totalStudents, 1)) * 100)}% engagement rate</p>
              </div>
            </div>
          </div>

          {/* Key Achievements */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Achievements</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                <Award className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-900">Revenue Milestone</p>
                  <p className="text-xs text-green-700">Reached $80K total revenue</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                <Target className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Session Quality</p>
                  <p className="text-xs text-blue-700">Maintained 4.7+ avg rating</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
                <Clock className="h-5 w-5 text-purple-600" />
                <div>
                  <p className="text-sm font-medium text-purple-900">Response Time</p>
                  <p className="text-xs text-purple-700">Improved to 15min average</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <button className="w-full text-left p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
                <p className="text-sm font-medium text-blue-900">Generate Report</p>
                <p className="text-xs text-blue-700">Create performance summary</p>
              </button>
              <button className="w-full text-left p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors">
                <p className="text-sm font-medium text-green-900">Add Counselor</p>
                <p className="text-xs text-green-700">Onboard new team member</p>
              </button>
              <button className="w-full text-left p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors">
                <p className="text-sm font-medium text-purple-900">Review Settings</p>
                <p className="text-xs text-purple-700">Update agency preferences</p>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}